/**
 * Collect Meta CAPI tracking context to pass into Stripe create-checkout
 * endpoints from apps/app. The cookies (_fbc, _fbp, mediloop_vid) are
 * scoped to .mediloop.ai so they cross the subdomain hop from the
 * marketing site, which means we can read them here too.
 *
 * The Stripe webhook in apps/server uses these to fire an authoritative
 * server-side Meta CAPI Purchase event when checkout completes —
 * matching the same browser session Meta saw at ad-click time.
 *
 * Mirrors apps/web/src/lib/tracking/checkoutContext.js but in TypeScript
 * for the React app side.
 */

export interface MetaTrackingContext {
  fbc?: string;
  fbp?: string;
  fbclid?: string;
  visitorId?: string;
  oppref?: string;
  obref?: string;
  /** GA4 client id from the `_ga` cookie (scoped to .mediloop.ai). */
  gaClientId?: string;
  /** GA4 session id from the `_ga_NRG53Y2M9M` cookie. */
  gaSessionId?: string;
}

const GA4_MEASUREMENT_ID = 'G-NRG53Y2M9M';

export function getMetaTrackingContext(): MetaTrackingContext {
  if (typeof document === 'undefined') return {};

  const out: MetaTrackingContext = {};
  const fbc = readCookie('_fbc');
  const fbp = readCookie('_fbp');
  const visitorId = readCookie('mediloop_vid');
  const fbclid = readURLParam('fbclid');
  const oppref =
    readURLParam('oppref') ||
    readCookie('mediloop_oppref') ||
    readCookie('__oppref');
  const obref = readCookie('__obref') || readCookie('mediloop_obref');

  if (fbc) out.fbc = fbc;
  if (fbp) out.fbp = fbp;
  if (visitorId) out.visitorId = visitorId;
  if (fbclid) out.fbclid = fbclid;
  if (oppref) out.oppref = oppref;
  if (obref) out.obref = obref;

  // GA4 ids, stored in Stripe metadata so an in-app checkout could be tied
  // to a GA4 session. The webhook only reports acquisition purchases to GA4
  // today, so for the addon / upgrade flows this is bookkeeping.
  const ga = readCookie('_ga');
  const cid = ga ? ga.match(/^GA\d\.\d\.(\d+\.\d+)$/) : null;
  if (cid) out.gaClientId = cid[1];
  const gs = readCookie(`_ga_${GA4_MEASUREMENT_ID.replace(/^G-/, '')}`);
  if (gs) {
    const parts = gs.split('.');
    const sid = parts.length > 2 ? parts[2].match(/^s?(\d{6,})/) : null;
    if (sid) out.gaSessionId = sid[1];
  }
  return out;
}

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch {
    return null;
  }
}

function readURLParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}
